import { PlusOutlined } from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tooltip,
  Upload,
  notification
} from "antd"
import type { BaseOptionType } from "antd/es/select"
import type { RcFile, UploadFile } from "antd/es/upload/interface"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { notificationOptions } from "../../../../../../../constants/notification.config.ts"
import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useDateFormatter } from "../../../../../../../hooks/use-date-formatter.tsx"
import { validateFile } from "../../../../../../../utils/file-validation.ts"
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_MB
} from "../../../../../../Album/attachment.constants.ts"
import { AttachmentModels } from "../../../../../../Album/types/attachment.types.ts"
import { uploadProjectAttachment } from "../../../../../redux/project-attachments/project-attachments.thunks.ts"
import {
  createPayment,
  fetchFinancialTransactionItemsFilterOptions,
  updatePayment
} from "../../../../../redux/project-finance/project-finance.thunks.ts"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import { QuickAddInvoiceDrawer } from "./QuickAddInvoiceDrawer.tsx"
import type { PaymentModalState } from "./payment-modal.types.ts"
import { partitionUploadResults } from "./quick-add-invoice.utils.ts"

interface Props {
  state: PaymentModalState
  record: FinancialTransactionItem
  projectId: string
  userId: string
  invoiceCreateAllowed?: boolean
  invoiceSelfAssigned?: boolean
  uploadFileAllowed?: boolean
  onClose: () => void
  onSaved?: () => void
}

interface FormValues {
  amount: number
  payment_date?: Dayjs
  payment_method: string
  payment_details: string
  invoice?: string | null
}

// The picker yields a date only, but the backend stores an instant and orders
// the payments history by it. Send the chosen day carrying the current time of
// day, resolved in the user's timezone — a bare "YYYY-MM-DD" is read by the API
// as midnight UTC, which renders as the previous day on any negative offset and
// collapses same-day payments onto one timestamp.
//
// `stored` is the instant already on the payment: when an edit leaves the day
// untouched it is returned as-is, so changing an amount cannot rewrite the time.
const toPaymentInstant = (
  picked: Dayjs | undefined,
  timezone: string,
  stored?: string
) => {
  if (!picked) return undefined

  if (stored && dayjs.utc(stored).tz(timezone).isSame(picked, "day")) {
    return dayjs.utc(stored).format()
  }

  const now = dayjs().tz(timezone)
  return picked
    .tz(timezone, true)
    .hour(now.hour())
    .minute(now.minute())
    .second(now.second())
    .utc()
    .format()
}

export const PaymentFormModal: React.FC<Props> = ({
  state,
  record,
  projectId,
  userId,
  invoiceCreateAllowed,
  invoiceSelfAssigned,
  uploadFileAllowed,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { timezone, dateFormat } = useDateFormatter()
  const [form] = Form.useForm<FormValues>()
  const [submitting, setSubmitting] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [invoiceOptions, setInvoiceOptions] = useState<BaseOptionType[]>([])
  const [extraInvoiceOptions, setExtraInvoiceOptions] = useState<
    BaseOptionType[]
  >([])
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false)
  const amountValue = Form.useWatch("amount", form)

  const paymentMethods = [
    { value: "ach_wired", label: t("finance.paymentModal.methods.ach_wired") },
    { value: "check", label: t("finance.paymentModal.methods.check") },
    {
      value: "cash_deposit",
      label: t("finance.paymentModal.methods.cash_deposit")
    },
    { value: "credit", label: t("finance.paymentModal.methods.credit") },
    { value: "debit", label: t("finance.paymentModal.methods.debit") }
  ]

  const planned = parseFloat(record.value)
  const paid = parseFloat(record.exercised_value ?? "0")
  const remaining = Math.max(planned - paid, 0)

  useEffect(() => {
    if (state.mode === "edit") {
      const p = state.payment
      form.setFieldsValue({
        amount: Math.abs(parseFloat(p.value)),
        payment_date: p.payment_date ? dayjs.utc(p.payment_date).tz(timezone) : undefined,
        payment_method: p.payment_method,
        payment_details: p.payment_details ?? "",
        invoice: p.invoice ?? null
      })
    } else if (state.mode === "complete") {
      form.setFieldsValue({
        amount: remaining,
        payment_method: record.payment_method ?? "ach_wired",
        payment_details: record.payment_details ?? ""
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        payment_method: "ach_wired"
      })
    }
  }, [state, record.id])

  useEffect(() => {
    let active = true
    dispatch(fetchFinancialTransactionItemsFilterOptions({ project_id: projectId }))
      .unwrap()
      .then((opts) => {
        if (active) setInvoiceOptions(opts["invoices"] ?? [])
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [projectId, dispatch])

  const handleInvoiceCreated = (invoice: ProjectInvoice) => {
    setExtraInvoiceOptions((prev) => [
      { value: invoice.id, label: invoice.invoice_number },
      ...prev.filter((o) => o.value !== invoice.id)
    ])
    form.setFieldValue("invoice", invoice.id)
    setInvoiceDrawerOpen(false)
  }

  const stageFile = (file: RcFile) => {
    const valid = validateFile(file, {
      maxSize: ATTACHMENT_MAX_SIZE_MB,
      allowedExtensions: ATTACHMENT_ALLOWED_EXTENSIONS,
      defaultResult: true
    })

    if (valid === true) {
      setFileList((prev) => [
        ...prev,
        { uid: file.uid, name: file.name, originFileObj: file }
      ])
    }

    return false
  }

  const removeFile = (target: UploadFile) => {
    if (target.status === "done") return false
    setFileList((prev) => prev.filter((f) => f.uid !== target.uid))
    return false
  }

  const uploadStagedFiles = async (paymentId: string) => {
    const pending = fileList.filter((f) => f.status !== "done" && f.originFileObj)
    if (pending.length === 0) return

    const results = await Promise.allSettled(
      pending.map((f) =>
        dispatch(
          uploadProjectAttachment({
            data: {
              model_type: AttachmentModels.PARTIAL_TRANSACTION_ITEM,
              object_id: paymentId,
              project_id: projectId,
              file: f.originFileObj as RcFile
            },
            silent: true
          })
        ).unwrap()
      )
    )

    const { failedNames } = partitionUploadResults(pending, results)
    if (failedNames.length > 0) {
      notification.warning({
        ...notificationOptions.warning,
        message: t("finance.paymentModal.uploadPartialError", {
          count: failedNames.length
        })
      })
    }
  }

  const submit = async () => {
    const stored =
      state.mode === "edit" ? state.payment?.payment_date : undefined
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    const body = {
      amount: Number(values.amount).toFixed(2),
      payment_date: toPaymentInstant(values.payment_date, timezone, stored),
      payment_method: values.payment_method,
      payment_details: values.payment_details ?? "",
      invoice: values.invoice ?? null
    }

    setSubmitting(true)
    try {
      if (state.mode === "edit") {
        await dispatch(
          updatePayment({ transactionId: record.id, paymentId: state.payment.id, body })
        ).unwrap()
        await uploadStagedFiles(state.payment.id)
      } else {
        const created = await dispatch(
          createPayment({ transactionId: record.id, body })
        ).unwrap()
        await uploadStagedFiles(created.id)
      }
      onSaved?.()
      onClose()
    } catch {
      // intentionally empty — errors surface via global error handling
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    state.mode === "edit"
      ? t("finance.paymentModal.title.edit")
      : state.mode === "complete"
        ? t("finance.paymentModal.title.complete")
        : t("finance.paymentModal.title.create")

  return (
    <>
      <Modal
        open
        onCancel={onClose}
        onOk={() => submit()}
        okButtonProps={{ loading: submitting }}
        title={title}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label={t("finance.paymentModal.amount")}
            rules={[
              {
                required: true,
                message: t("finance.paymentModal.validation.required")
              },
              {
                validator: (_, v) =>
                  Number(v) > 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t("finance.paymentModal.validation.amountPositive"))
                      )
              }
            ]}
          >
            <InputNumber step={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="payment_date" label={t("finance.paymentModal.date")}>
            <DatePicker style={{ width: "100%" }} format={dateFormat} />
          </Form.Item>
          <Form.Item
            name="payment_method"
            label={t("finance.paymentModal.method")}
            rules={[
              {
                required: true,
                message: t("finance.paymentModal.validation.required")
              }
            ]}
          >
            <Select options={paymentMethods} />
          </Form.Item>
          <Form.Item name="payment_details" label={t("finance.paymentModal.details")}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="invoice" label={t("finance.paymentModal.invoice")}>
            <Select
              placeholder={t("finance.paymentModal.selectInvoice")}
              options={[...invoiceOptions, ...extraInvoiceOptions]}
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          {invoiceCreateAllowed && (
            <Flex justify="flex-end" style={{ marginTop: -12, marginBottom: 12 }}>
              <Tooltip title={t("finance.paymentModal.quickAddInvoice")}>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  aria-label={t("finance.paymentModal.quickAddInvoice")}
                  onClick={() => setInvoiceDrawerOpen(true)}
                >
                  {t("finance.paymentModal.quickAddInvoice")}
                </Button>
              </Tooltip>
            </Flex>
          )}
          {uploadFileAllowed && (
            <Form.Item label={t("finance.paymentModal.attachments")}>
              <Upload
                multiple
                fileList={fileList}
                accept={ATTACHMENT_ACCEPT}
                beforeUpload={stageFile}
                onRemove={removeFile}
              >
                <Button icon={<PlusOutlined />}>
                  {t("finance.paymentModal.uploadFiles")}
                </Button>
              </Upload>
            </Form.Item>
          )}
        </Form>
      </Modal>
      {invoiceCreateAllowed && (
        <QuickAddInvoiceDrawer
          open={invoiceDrawerOpen}
          onClose={() => setInvoiceDrawerOpen(false)}
          onCreated={handleInvoiceCreated}
          userId={userId}
          selfAssigned={invoiceSelfAssigned}
          paymentAmount={amountValue != null ? Number(amountValue) : null}
          preselectedLineId={record.id}
        />
      )}
    </>
  )
}
